<template>
  <div
    class="app-collapse"
    :class="{
      closed: state === STATE_CLOSED,
      closing: state === STATE_CLOSING,
      opening: state === STATE_OPENING,
      opened: state === STATE_OPENED,
    }"
  >
    <div
      ref="contentWrapper"
      class="content-wrapper"
      :style="{ maxHeight: height }"
    >
      <div ref="content">
        <slot
          v-if="!destroyOnClose || (destroyOnClose && state !== STATE_CLOSED)"
        />
      </div>
    </div>
  </div>
</template>

<script>
const STATE_CLOSED = 0
const STATE_CLOSING = 1
const STATE_OPENING = 2
const STATE_OPENED = 3

export default {
  name: 'AppCollapse',

  emits: ['update:modelValue', 'opening', 'closing', 'opened', 'closed'],

  data() {
    this.STATE_CLOSED = STATE_CLOSED
    this.STATE_CLOSING = STATE_CLOSING
    this.STATE_OPENING = STATE_OPENING
    this.STATE_OPENED = STATE_OPENED

    return {
      resizeObserver: undefined,
      state: STATE_CLOSED,
      height: 0,
    }
  },

  props: {
    destroyOnClose: {
      type: Boolean,
      default: false,
    },

    modelValue: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    isCollapsed() {
      return [STATE_CLOSED, STATE_CLOSING].includes(this.state)
    },
  },

  watch: {
    modelValue: {
      immediate: true,
      handler(value) {
        switch (value) {
          case true:
            this.open()
            break

          case false:
            this.close()
            break
        }
      },
    },
  },

  beforeUnmount() {
    this.resizeObserver.disconnect()
  },

  mounted() {
    const element = this.$refs.contentWrapper

    this.resizeObserver = new ResizeObserver(() => {
      if (!this.isCollapsed) this.updateHeight()
    })

    this.resizeObserver.observe(this.$refs.content)

    element.addEventListener('transitionend', event => {
      if (event.propertyName !== 'max-height') return

      switch (this.state) {
        case STATE_OPENING:
          this.$emit('opened', this)
          break

        case STATE_CLOSING:
          this.$emit('closed', this)
          break
      }
    })
  },

  beforeCreate() {
    this.$on('opening', () => {
      this.updateHeight()
      this.state = STATE_OPENING
      this.$emit('update:modelValue', true)
    })
      .$on('opened', () => {
        this.state = STATE_OPENED
      })
      .$on('closing', () => {
        this.height = 0
        this.state = STATE_CLOSING
      })
      .$on('closed', () => {
        this.state = STATE_CLOSED
        this.$emit('update:modelValue', false)
      })
  },

  methods: {
    updateHeight() {
      const { content } = this.$refs

      if (!content) return

      this.height = `${content.scrollHeight}px`
    },

    async open() {
      return new Promise(resolve => {
        if (!this.isCollapsed) return resolve()

        this.$once('opened', resolve)
        this.$emit('opening', this)
      })
    },

    async close() {
      return new Promise(resolve => {
        if (this.isCollapsed) return resolve()

        this.$once('closed', resolve)
        this.$emit('closing', this)
      })
    },

    async toggle() {
      return [STATE_CLOSING, STATE_CLOSED].includes(this.state)
        ? this.open()
        : this.close()
    },
  },
}
</script>

<style lang="scss" scoped>
.app-collapse {
  width: 100%;

  > .content-wrapper {
    will-change: max-height, overflow-y;
    max-height: 0;
    transition: max-height 0.5s ease-in-out;
  }

  &.closing,
  &.closed {
    > .content-wrapper {
      overflow-y: hidden;
    }
  }
}
</style>
